import { FormFieldset } from 'components/FormButton'
import { PasswordError } from 'components/SimpleFlowSignupForm/PasswordError'
import { HStack, Stack } from 'components/Stack'
import { TermsAndPrivacyLinks } from 'components/TermsAndPrivacyLinks'
import { Caption, Text } from 'components/Typography'
import { AuthSocial } from 'components/auth/authSocial/AuthSocial'
import { useSignupLogic } from 'components/auth/register/useSignupLogic'
import { LinksWrapper } from 'components/auth/signupCard/SignupCard'
import { Button } from 'components/button/Button'
import { Card } from 'components/card/Card'
import { TextField } from 'components/directive/form/TextField'
import { Fieldset } from 'components/fieldset/Fieldset'
import { Icon } from 'components/icon'
import { SimpleFlowDirectiveType } from 'logic/directive/types'
import { Trans, useTranslation } from 'next-i18next'
import { IFormPayload } from 'pages/signup'
import { useForm } from 'react-hook-form'
import { colors } from 'utils/colors'
import { useFieldValidation } from 'utils/i18n/useFieldValidation'
import { useAolPromotionCode, useStoredPromoCodeLogic, useStoredPromoCodeSwr } from 'utils/promo'
import { onGoogleFailureToastError } from 'utils/social'
import { PromoCodeIsUsed, PromoCodeNotFound } from 'components/aolLandingPage/ErrorMessages'
import { useFillEmail } from 'components/SimpleFlowSignupForm/utils/hooks'
import { useEffect } from 'react'

export function SimpleFlowSignupForm({
  flowDirective,
  aol,
  external,
  promo,
}: {
  flowDirective?: SimpleFlowDirectiveType
  aol?: boolean
  external?: boolean
  promo?: string
}) {
  const validation = useFieldValidation()

  const form = useForm<IFormPayload>({
    defaultValues: {
      email: '',
      password: '',
      is_promocode: false,
      code: '',
    },
    mode: 'onTouched',
    criteriaMode: 'all',
  })

  useEffect(() => {
    if (promo) {
      form.setValue('code', promo)
      form.setValue('is_promocode', true)
    }
  }, [promo])

  const setValues = form.setValue
  const formEmail = form.getValues('email')
  const signupLogic = useSignupLogic(form, undefined, undefined, flowDirective, aol)

  const isPromoCode = form.watch('is_promocode')

  const { data: promoCode } = useStoredPromoCodeSwr()
  const { data: codeValidation } = useAolPromotionCode(promoCode, aol)

  useFillEmail({ aol, formEmail, setValues })

  useStoredPromoCodeLogic({
    onValidatedCode(code) {
      if (!aol) {
        form.setValue('is_promocode', true)
      }
      form.setValue('code', code)
    },
  })

  const displayPromoCodeIsUsed =
    codeValidation?.data?.offers.every(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      // @ts-ignore
      (offer) => offer.error_code === 'promo_code_usage_exceeded'
    ) || signupLogic.promo === 'promo_code_usage_exceeded'

  const displayPromoCodeNotFound =
    codeValidation?.error?.errorCode === 'entity_not_found_promo_code_not_found' ||
    signupLogic.promo === 'entity_not_found_promo_code_not_found'

  const { t } = useTranslation('entrance')
  const { t: commonT } = useTranslation('common')
  return (
    <section>
      {signupLogic.jsx}
      <Stack space="1.5rem">
        {displayPromoCodeNotFound && <PromoCodeNotFound />}
        {displayPromoCodeIsUsed && <PromoCodeIsUsed />}
        <Card css={{ padding: '2.5rem', boxShadow: external ? 'none' : 'var(--card-shadow)' }}>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              await signupLogic.onSubmit()
            }}
          >
            <Stack space="1.5rem" css={{ marginBottom: 0 }}>
              <Stack space="1rem">
                <Caption css={{ gridColumn: 'span 2' }}>
                  {t('entrance.trust_signup.form.caption')}
                </Caption>
                <Stack space="0.625rem">
                  <TextField
                    form={form}
                    name="email"
                    label={t('entrance.trust_signup.form.email_label')}
                    register={{ ...validation.required(), ...validation.email() }}
                    type="email"
                  />
                  <div css={{ position: 'relative' }}>
                    <FormFieldset form={form}>
                      <Fieldset>
                        <input
                          {...form.register('password', validation.passwordValidation())}
                          type="password"
                          autoComplete="new-password"
                          placeholder="&nbsp;"
                        />
                        <label>{t('entrance.trust_signup.form.password_label')}</label>
                      </Fieldset>
                      <PasswordError />
                    </FormFieldset>
                  </div>

                  <Text variant="bodySmall">
                    {commonT('common.form.strong_password_encouragement')}
                  </Text>
                  {isPromoCode && (
                    <FormFieldset form={form}>
                      <Fieldset>
                        <input {...form.register('code')} type="text" placeholder="&nbsp;" />
                        <label>{t('entrance.trust_signup.form.promo_code_label')}</label>
                      </Fieldset>
                    </FormFieldset>
                  )}
                </Stack>

                <LinksWrapper
                  loginHref={`/login?flow=${flowDirective}`}
                  form={form}
                  isPromoCode={isPromoCode}
                  aol={aol}
                  external={external}
                />
              </Stack>

              <Button
                type="submit"
                variant="primary"
                size="large"
                block
                loading={form.formState.isSubmitting}
              >
                {aol
                  ? t('entrance.trust_signup.form.submit_button_aol')
                  : flowDirective === 'TRUST'
                  ? t('entrance.trust_signup.form.submit_button_trust')
                  : t('entrance.trust_signup.form.submit_button_will')}
              </Button>
              <Stack space="1rem">
                <Caption css={{ lineHeight: '200%', textAlign: 'center' }}>
                  {t('entrance.trust_signup.form.or')}
                </Caption>

                <AuthSocial
                  onFacebook={signupLogic.onFacebook}
                  onGoogleSuccess={signupLogic.onGoogle}
                  onGoogleFailure={(res) =>
                    onGoogleFailureToastError(res, 'failed_to_create_account')
                  }
                  block
                  areButtonsSignup
                />
              </Stack>
              <Text variant="bodySmall" css={{ textAlign: 'center' }}>
                <Trans
                  t={t}
                  i18nKey="entrance.trust_signup.form.terms_and_conditions"
                  components={[<TermsAndPrivacyLinks key={0} />]}
                />
              </Text>
            </Stack>
          </form>
        </Card>
        <HStack css={{ alignSelf: 'center' }}>
          <Icon name="LockClosed" color={colors.gray[500]} />
          <Caption>{t('entrance.trust_signup.form.encryption_text')}</Caption>
        </HStack>
      </Stack>
    </section>
  )
}
